import type { ClassDef, Content, Rect, RoomDef, RoomGrid, WaveDef } from '../data/schema';
import { DEFAULT_BODY_HEIGHT_FACTOR } from './config';
import { footprintFits, walkOf, type NavField } from './map/nav';

export { footprintFits };
import type { Rng } from './rng';
import { MAX_SPELL_SLOTS, xpToNext } from './progression';
import { maxHpWith, ZERO_META_BONUSES, type MetaBonuses } from './meta/bonuses';
import { availableSpells } from './meta/unlocks';
import { ZERO_MODS, ZERO_STATS, type AnyEntity, type CoreEvent, type Player, type Vec2 } from './types';

/** `nav` : champ de distances vers le joueur (salles à grille), recalculé quand le joueur change de cellule. */
export interface World { content: Content; room: RoomDef; rng: Rng; entities: Map<number, AnyEntity>; nextId: number; time: number; events: CoreEvent[]; wave: number; waveSpawned: boolean; cleared: boolean; waves: WaveDef[]; hpMult: number; nav: NavField | null; meta: MetaBonuses; monsterSpeedPct: number; }

export function createWorld(content: Content, room: RoomDef, rng: Rng, opts: { waves?: WaveDef[]; hpMult?: number; meta?: MetaBonuses; monsterSpeedPct?: number } = {}): World {
  return { content, room, rng, entities: new Map(), nextId: 1, time: 0, events: [], wave: 0, waveSpawned: false, cleared: false, waves: opts.waves ?? room.waves, hpMult: opts.hpMult ?? 1, nav: null, meta: opts.meta ?? ZERO_META_BONUSES, monsterSpeedPct: opts.monsterSpeedPct ?? 0 };
}

export function spawn<T extends AnyEntity>(world: World, e: Omit<T, 'id'>): T {
  const ent = { ...e, id: world.nextId++ } as T;
  world.entities.set(ent.id, ent);
  return ent;
}

export function spawnPlayer(world: World, cls: ClassDef, at: Vec2): Player {
  const meta = world.meta;
  const hpMax = maxHpWith(cls.hp + cls.baseStats.vitalite, meta);
  const level = 1 + meta.startLevel;
  const player = spawn<Player>(world, {
    kind: 'player', team: 'player', defId: cls.id, sprite: cls.sprite,
    pos: { ...at }, vel: { x: 0, y: 0 }, radius: cls.radius, height: cls.height ?? cls.radius * DEFAULT_BODY_HEIGHT_FACTOR, facing: { x: 1, y: 0 },
    hp: hpMax, hpMax, alive: true, anim: 'idle', animTime: 0,
    stats: { ...cls.baseStats }, passiveStats: { ...ZERO_STATS }, passives: {}, equipment: {}, speedPct: meta.speedPct,
    level, xp: 0, xpNext: xpToNext(level), kamas: meta.startKamas,
    primary: { id: cls.primarySpell, level: 1, mods: { ...ZERO_MODS } }, spells: [],
    cooldowns: {}, attackCooldown: 0, rerolls: 1 + meta.rerolls, shield: null, buffs: [], lifestealPct: 0, summonPct: 0,
  });
  // sorts de départ : ceux de la liste qui sont disponibles, hors sort principal, sans doublon, dans la limite des emplacements
  const allowed = new Set(availableSpells(world.content, cls.id, meta));
  for (const id of meta.startSpells) {
    if (player.spells.length >= MAX_SPELL_SLOTS) break;
    if (id === cls.primarySpell || !allowed.has(id) || !world.content.spells[id] || player.spells.some((s) => s.id === id)) continue;
    player.spells.push({ id, level: 1, mods: { ...ZERO_MODS } });
    player.cooldowns[id] = 0;
  }
  return player;
}

/** Réinjecte un joueur existant (run en cours) dans un nouveau monde, à la position donnée. */
export function adoptPlayer(world: World, p: Player, at: Vec2): void {
  world.entities.set(p.id, p);
  world.nextId = Math.max(world.nextId, p.id + 1);
  p.pos = { ...at };
  p.vel = { x: 0, y: 0 };
  p.anim = 'idle';
  p.animTime = 0;
}

/** Hauteur du corps (pieds -> sommet) d'une entité ; repli sur radius * 2.2 si elle n'est pas renseignée. */
export function bodyHeight(e: { radius: number; height?: number }): number {
  return e.height !== undefined && Number.isFinite(e.height) && e.height > 0 ? e.height : e.radius * DEFAULT_BODY_HEIGHT_FACTOR;
}

export function getPlayer(world: World): Player | undefined {
  for (const e of world.entities.values()) if (e.kind === 'player') return e;
  return undefined;
}

// Si le rectangle est plus étroit que 2*radius sur un axe, on centre le point sur cet axe.
function clampAxis(p: number, start: number, size: number, radius: number): number {
  const lo = start + radius;
  const hi = start + size - radius;
  if (lo > hi) return start + size / 2;
  return Math.min(Math.max(p, lo), hi);
}

function clampToRects(pos: Vec2, radius: number, rects: Rect[]): Vec2 {
  let best: Vec2 | null = null;
  let bestD = Infinity;
  for (const r of rects) {
    const x = clampAxis(pos.x, r.x, r.w, radius);
    const y = clampAxis(pos.y, r.y, r.h, radius);
    const d = (x - pos.x) ** 2 + (y - pos.y) ** 2;
    if (d < bestD) { bestD = d; best = { x, y }; }
  }
  return best ?? { ...pos };
}

const RINGS: [number, number][] = [];
for (let d = 3; d <= 96; d += 3) RINGS.push([d, 16]);
for (let d = 120; d <= 600; d += 24) RINGS.push([d, 32]);

const SLIDE_DEG = [15, 30, 45, 60, 75];

function clampToGrid(pos: Vec2, radius: number, grid: RoomGrid, from?: Vec2): Vec2 {
  const walk = walkOf(grid);
  if (footprintFits(walk, pos, radius)) return { ...pos };
  if (from && footprintFits(walk, from, radius)) {
    // Glissement : on tourne le déplacement de 15 en 15 degrés (des deux côtés), le premier qui passe gagne.
    // Indispensable contre les bords en dents de scie des losanges, où la projection seule reste coincée.
    const dx = pos.x - from.x, dy = pos.y - from.y;
    for (const deg of SLIDE_DEG) {
      const k = Math.max(0.5, Math.cos((deg * Math.PI) / 180));
      for (const sgn of [1, -1]) {
        const a = (sgn * deg * Math.PI) / 180, c = Math.cos(a), sn = Math.sin(a);
        const q = { x: from.x + (dx * c - dy * sn) * k, y: from.y + (dx * sn + dy * c) * k };
        if (footprintFits(walk, q, radius)) return q;
      }
    }
    return { ...from };
  }
  // Position valide la plus proche : anneaux croissants (3 px jusqu'à 96 px, puis 24 px jusqu'à 600 px).
  for (const [d, n] of RINGS) {
    let best: Vec2 | null = null, bestD = Infinity;
    for (let k = 0; k < n; k++) {
      const a = (k / n) * Math.PI * 2;
      const q = { x: pos.x + Math.cos(a) * d, y: pos.y + Math.sin(a) * d };
      if (!footprintFits(walk, q, radius)) continue;
      const dd = (q.x - pos.x) ** 2 + (q.y - pos.y) ** 2;
      if (dd < bestD) { bestD = dd; best = q; }
    }
    if (best) return best;
  }
  return { ...pos };
}

/**
 * Ramène `pos` sur la zone marchable de la salle : masque de cellules si la salle a une grille, sinon rectangles.
 * `from` (position actuelle, optionnelle) active le glissement le long des obstacles pour un déplacement continu ;
 * sans `from` (téléportation, repoussement), on prend la position valide la plus proche.
 */
export function clampToWalkable(pos: Vec2, radius: number, room: Pick<RoomDef, 'walkable' | 'grid'>, from?: Vec2): Vec2 {
  return room.grid ? clampToGrid(pos, radius, room.grid, from) : clampToRects(pos, radius, room.walkable);
}

export function entitiesOfKind<T extends AnyEntity>(world: World, kind: T['kind']): T[] {
  const out: T[] = [];
  for (const e of world.entities.values()) if (e.kind === kind) out.push(e as T);
  return out;
}
